import { TemplateTargetType } from '@compass/client/types/template';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Textarea } from '@components/ui/textarea';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { useRenderTemplate, useTemplates } from '@/hooks/useTemplates';
import { useCreateText } from '@/hooks/useText';
import { useStore } from '@/store/store';
import type { ActionDataParams } from '@/types/actions';

import { isValidPhoneNumber } from '../utils/phone';
import { sanitizeTextMessage } from '../utils/text';

interface SendTextFormData {
  to: string;
  content: string;
}

const CUSTOM_TEMPLATE_ID = 'custom';

interface ActionTextDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecordId: string;
  action: string;
  onActionSelect: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  title?: string;
  dialogDescription?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export function ActionTextDialog({
  isOpen,
  onClose,
  caseRecordId,
  action,
  onActionSelect,
  title = 'Send Text',
  dialogDescription = 'Details for text',
  confirmButtonText = 'Send Text',
  cancelButtonText = 'Cancel',
}: ActionTextDialogProps) {
  const createText = useCreateText();
  const renderTemplate = useRenderTemplate();
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Fetch text templates scoped to the user's program
  const { data: templatesData, isLoading: isLoadingTemplates } = useTemplates({
    programId: selectedProgramId ?? undefined,
    targetType: TemplateTargetType.TEMPLATE_TARGET_TYPE_TEXT,
    enabled: isOpen,
  });

  const templates = templatesData?.templates ?? [];
  const hasTemplates = templates.length > 0;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(CUSTOM_TEMPLATE_ID);
  const [formData, setFormData] = useState<SendTextFormData>({
    to: '',
    content: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
      setFormData({ to: '', content: '' });
      setErrorMessage(null);
    }
  }, [isOpen]);

  // https://help.twilio.com/articles/360033806753-Maximum-Message-Length-with-Twilio-Programmable-Messaging
  const MAX_MESSAGE_LENGTH = 320;

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);

      if (templateId === CUSTOM_TEMPLATE_ID) {
        // User chose custom - clear the form
        setFormData(prev => ({
          ...prev,
          content: '',
        }));
        return;
      }

      // Render the template with case record context
      renderTemplate.mutate(
        { templateId, caseRecordId },
        {
          onSuccess: data => {
            const renderedContent = data.rendered?.text?.content ?? '';
            setFormData(prev => ({
              ...prev,
              content: renderedContent,
            }));
          },
          onError: error => {
            console.error('Failed to render template:', error);
            setErrorMessage('Failed to load template. Please try again or use a custom template.');
          },
        },
      );
    },
    [caseRecordId, renderTemplate],
  );

  const handleConfirm = useCallback(() => {
    // Validate required fields
    if (!formData.to.trim() || !formData.content.trim()) {
      return;
    }
    setErrorMessage(null);
    // Validate phone number
    if (!isValidPhoneNumber(formData.to)) {
      return;
    }
    // Validate message length
    if (formData.content.length > MAX_MESSAGE_LENGTH) {
      return;
    }

    // Create the text entity
    createText.mutate(
      {
        caseRecordId,
        data: {
          case_record_id: caseRecordId,
          to: formData.to.trim(),
          content: sanitizeTextMessage(formData.content),
          // TODO: Move from number to backend - should be determined based on program configuration
          from: '+19014419449',
        },
      },
      {
        onSuccess: data => {
          const newTextId = data.text?.id;
          if (!newTextId) {
            console.error('Text created but no ID returned');
            return;
          }

          // Reset state
          setFormData({ to: '', content: '' });
          setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
          // Close dialog
          onClose();
          // Trigger action with text_id
          onActionSelect(action, caseRecordId, { text_id: newTextId });
        },
        onError: error => {
          console.error('Failed to create text:', error);
          setErrorMessage('Failed to send text. Please try again.');
        },
      },
    );
  }, [formData, caseRecordId, action, onActionSelect, onClose, createText]);

  const handleClose = useCallback(() => {
    // Reset state on cancel
    setFormData({ to: '', content: '' });
    setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback((field: keyof SendTextFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const isPhoneValid = formData.to.trim() === '' || isValidPhoneNumber(formData.to);
  const isFormValid =
    formData.to.trim() !== '' &&
    isValidPhoneNumber(formData.to) &&
    formData.content.trim() !== '' &&
    formData.content.length <= MAX_MESSAGE_LENGTH;

  const isSubmitting = createText.isPending;
  const isRenderingTemplate = renderTemplate.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Template Selection - Loading State */}
          {isLoadingTemplates && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">Template</label>
              <div className="flex h-10 items-center rounded-md border border-input bg-background px-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
              </div>
            </div>
          )}

          {/* Template Selection */}
          {!isLoadingTemplates && hasTemplates && (
            <div className="grid gap-2">
              <label htmlFor="template-select" className="text-sm font-medium">
                Template
              </label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
                disabled={isLoadingTemplates || isSubmitting}
              >
                <SelectTrigger id="template-select">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_TEMPLATE_ID}>
                    <span className="text-muted-foreground">Custom</span>
                  </SelectItem>
                  {templates
                    .filter(template => template.id)
                    .map(template => (
                      <SelectItem key={template.id} value={template.id!}>
                        {template.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Loading state when rendering template */}
          {isRenderingTemplate && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading template...</span>
            </div>
          )}

          {/* Form fields - shown when not loading template */}
          {!isRenderingTemplate && (
            <>
              {/* Recipient */}
              <div className="grid gap-2">
                <label htmlFor="text-to" className="text-sm font-medium">
                  Recipient
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  id="text-to"
                  type="tel"
                  value={formData.to}
                  onChange={e => updateField('to', e.target.value)}
                  placeholder="Enter recipient phone number"
                  className={!isPhoneValid ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                {!isPhoneValid && (
                  <span className="text-xs text-destructive">
                    Please enter a valid phone number
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="text-content" className="text-sm font-medium">
                    Message
                    <span className="text-destructive ml-1">*</span>
                  </label>
                  <span
                    className={`text-xs ${
                      formData.content.length > MAX_MESSAGE_LENGTH
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {formData.content.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                <Textarea
                  id="text-content"
                  value={formData.content}
                  onChange={e => updateField('content', e.target.value)}
                  placeholder="Enter message"
                  className={`min-h-[100px] ${
                    formData.content.length > MAX_MESSAGE_LENGTH ? 'border-destructive' : ''
                  }`}
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </div>

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || isRenderingTemplate || !isFormValid}
          >
            {isSubmitting ? 'Processing...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
